import { Form, type FormInstance, message } from 'antd';
import type { NamePath } from 'antd/es/form/interface';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';

export type SubmitFormResult = { success: boolean; errorMessage?: ReactNode };

type RecursivePartial<T> = T extends object
  ? {
      [P in keyof T]?: T[P] extends (infer U)[]
        ? RecursivePartial<U>[]
        : T[P] extends object
          ? RecursivePartial<T[P]>
          : T[P];
    }
  : // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any;

export function useFormExpanded<T>({ form, successMessage }: { form: FormInstance<T>; successMessage?: string }) {
  const [errorMessage, setErrorMessage] = useState<ReactNode | undefined>(undefined);

  const submitForm = useCallback(
    async (onSubmit: (values: T) => Promise<SubmitFormResult>, overrideValues?: Partial<T>) => {
      try {
        const values = await form.validateFields();

        if (values && typeof values === 'object') {
          if ('__validation' in values) {
            delete values.__validation;
          }

          if ('__touched' in values) {
            delete values.__touched;
          }
        }

        const { success, errorMessage } = await onSubmit({ ...values, ...overrideValues });

        if (!success) {
          setErrorMessage(errorMessage);
        } else if (successMessage) {
          message.success(successMessage);
        }

        return success;
      } catch (error) {
        console.error('Form validation failed:', error);
        message.error('Review form errors');
        return false;
      }
    },
    [form, successMessage],
  );

  const clearErrorMessage = useCallback(() => {
    setErrorMessage(undefined);
  }, [setErrorMessage]);

  const setFormValue = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (path: NamePath, value: any) => {
      form.setFieldValue(path, value);
      form.setFieldValue('__touched', true);
    },
    [form],
  );

  const setFormValues = useCallback(
    (values: RecursivePartial<T>) => {
      form.setFieldsValue(values);
      form.setFieldValue('__touched', true);
    },
    [form],
  );

  return {
    submitForm,
    errorMessage,
    clearErrorMessage,
    setFormValue,
    setFormValues,
  };
}

export function useFormInstance<T>({ successMessage }: { successMessage?: string } = {}) {
  const form = Form.useFormInstance<T>();
  const expanded = useFormExpanded({ form, successMessage });

  return {
    form,
    ...expanded,
  };
}

export function useForm<T>({ successMessage }: { successMessage?: string } = {}) {
  const [form] = Form.useForm<T>();
  const touched = Boolean(Form.useWatch('__touched', form));
  const expanded = useFormExpanded({ form, successMessage });

  /**
   * Trigger the touched state to be true on user input
   */
  const onValuesChange = useCallback(() => {
    form.setFieldValue('__touched', true);
  }, [form]);

  const clearTouched = useCallback(() => {
    form.setFieldValue('__touched', false);
  }, [form]);

  return {
    form,
    touched,
    onValuesChange,
    clearTouched,
    ...expanded,
  };
}
